// BUILD-046 task 8.20: React binding for the two labelled counts (workCountsController.ts holds the logic and its tests).
//
// The counts are read only while `enabled` (the host's Caller summary is ready, so the new routes answer): with the routes disabled nothing is
// requested and the page stays the legacy page. They load when enabled, again when the Caller changes (cleared at once, then loaded for the new
// Caller), and on `shared-tasks:changed` (a Done, a claim, an assignment or an action by this Caller). Retry loads both again.
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { TASKS_CHANGED_EVENT, onCallerChanged, workApi } from '../../api/workShared';
import { createCountsController } from './workCountsController';
import type { CountsController, CountsState } from './workCountsController';

export function useWorkCounts(enabled: boolean): { state: CountsState; retry: () => void } {
  const ref = useRef<CountsController | null>(null);
  if (ref.current === null) ref.current = createCountsController({ getEpoch: () => workApi.getCallerEpoch(), getBell: () => workApi.getBell() });
  const controller = ref.current;
  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  useEffect(() => {
    if (!enabled) return undefined;
    void controller.load();
    const offCaller = onCallerChanged(() => {
      void controller.callerChanged();
    });
    const w = (globalThis as { window?: Window }).window;
    const onChanged = () => {
      void controller.load();
    };
    if (w) w.addEventListener(TASKS_CHANGED_EVENT, onChanged);
    return () => {
      offCaller();
      if (w) w.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
    };
  }, [controller, enabled]);
  return {
    state,
    retry: () => {
      void controller.load();
    },
  };
}
